import React from 'react'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { cn } from '../lib/utils'
import { resolveKpiIconClass, semanticKpiIconClass, type SemanticKpiTone } from '../../helpers/kpiIconClass'
import { DashboardMetricValue } from './dashboard-metric-value'

export type MetricCardSize = 'compact' | 'default'
export type MetricCardEmphasis = 'standard' | 'primary'
export type MetricCardTone = SemanticKpiTone | 'neutral'
export type MetricCardVariant = 'default' | 'compact' | 'operational' | 'financial'

export type MetricCardProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  title: React.ReactNode
  value?: string | number | null
  metricValue?: number | string | null
  isCurrency?: boolean
  currencySymbol?: string
  icon?: React.ElementType
  iconNode?: React.ReactNode
  iconClassName?: string
  tone?: MetricCardTone
  description?: React.ReactNode
  comparison?: React.ReactNode
  change?: React.ReactNode
  changeType?: 'positive' | 'negative' | 'neutral'
  deltaPct?: number | null
  invertDelta?: boolean
  headerAdornment?: React.ReactNode
  loading?: boolean
  noData?: boolean
  noDataLabel?: string
  unavailable?: boolean
  unavailableLabel?: string
  permissionDenied?: boolean
  permissionLabel?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
  size?: MetricCardSize
  emphasis?: MetricCardEmphasis
  variant?: MetricCardVariant
  titleHint?: string
  /** Backward-compatible dashboard API used by existing screens. */
  presentation?: 'default' | 'dashboard'
  kpiTier?: 'primary' | 'secondary'
}

function DeltaDisplay({
  deltaPct,
  invertDelta,
}: {
  deltaPct?: number | null
  invertDelta?: boolean
}) {
  if (deltaPct === undefined || deltaPct === null) return null

  const positive = deltaPct > 0
  const negative = deltaPct < 0
  const good = invertDelta ? negative : positive
  const bad = invertDelta ? positive : negative

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
        good && 'text-semantic-success',
        bad && 'text-semantic-danger',
        !good && !bad && 'text-muted-foreground',
      )}
    >
      {positive ? <ArrowUpRight className="h-3 w-3" aria-hidden="true" /> : null}
      {negative ? <ArrowDownRight className="h-3 w-3" aria-hidden="true" /> : null}
      {!positive && !negative ? <Minus className="h-3 w-3" aria-hidden="true" /> : null}
      {positive ? '+' : ''}
      {deltaPct}%
    </span>
  )
}

function LoadingValue() {
  return <span className="metric-card__skeleton h-8 w-28" aria-label="Loading" />
}

export function MetricCard({
  title,
  value,
  metricValue,
  isCurrency,
  currencySymbol,
  icon: Icon,
  iconNode,
  iconClassName,
  tone = 'info',
  description,
  comparison,
  change,
  changeType = 'neutral',
  deltaPct,
  invertDelta,
  headerAdornment,
  loading = false,
  noData = false,
  noDataLabel = '—',
  unavailable = false,
  unavailableLabel = '—',
  permissionDenied = false,
  permissionLabel = '—',
  onClick,
  size = 'default',
  emphasis,
  variant = 'default',
  titleHint,
  presentation = 'default',
  kpiTier,
  className,
  ...props
}: MetricCardProps) {
  const resolvedSize = kpiTier === 'secondary' || size === 'compact' || variant === 'compact' ? 'compact' : size
  const resolvedEmphasis = emphasis ?? (kpiTier === 'primary' ? 'primary' : 'standard')
  const iconTone = tone === 'neutral' ? 'muted' : tone
  const resolvedIconClassName = resolveKpiIconClass(iconClassName) ?? semanticKpiIconClass(iconTone)
  const isInteractive = Boolean(onClick)
  const effectiveMetricValue = metricValue !== undefined ? metricValue : value
  const isUnavailable = unavailable || permissionDenied
  const hasValue = effectiveMetricValue !== undefined && effectiveMetricValue !== null && effectiveMetricValue !== ''
  const isNoData = noData || (!hasValue && !permissionDenied && !unavailable)
  const hasComparison = Boolean(comparison) || (deltaPct !== undefined && deltaPct !== null)

  return (
    <div
      className={cn(
        'metric-card group',
        resolvedSize === 'compact' && 'metric-card--compact',
        resolvedEmphasis === 'primary' && 'metric-card--primary',
        variant === 'operational' && 'metric-card--operational',
        variant === 'financial' && 'metric-card--financial',
        presentation === 'dashboard' && 'metric-card--dashboard',
        isInteractive && 'metric-card--interactive',
        className,
      )}
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.currentTarget.click()
              }
            }
          : undefined
      }
      {...props}
    >
      <div className="metric-card__body">
        <div className="metric-card__heading">
          <div className="metric-card__title" title={titleHint}>
            {title}
          </div>
          {headerAdornment ? <div className="metric-card__adornment">{headerAdornment}</div> : null}
        </div>

        {description ? <p className="metric-card__description">{description}</p> : null}

        <div className="metric-card__value" aria-live={loading ? 'polite' : undefined}>
          {loading ? (
            <LoadingValue />
          ) : permissionDenied ? (
            <span className="metric-card__state">{permissionLabel}</span>
          ) : isNoData ? (
            <span className="metric-card__state">{noDataLabel}</span>
          ) : isUnavailable ? (
            <span className="metric-card__state">{unavailableLabel}</span>
          ) : (
            <DashboardMetricValue
              metricValue={metricValue}
              isCurrency={isCurrency}
              currencySymbol={currencySymbol}
              value={value ?? undefined}
              kpiTier={resolvedEmphasis === 'primary' ? 'primary' : 'secondary'}
              compact={resolvedSize === 'compact'}
              unavailable={false}
            />
          )}
        </div>

        {change || hasComparison ? (
          <div className="metric-card__meta">
            {change ? (
              <span
                className={cn(
                  'text-xs font-medium',
                  changeType === 'positive' && 'text-semantic-success',
                  changeType === 'negative' && 'text-semantic-danger',
                  changeType === 'neutral' && 'text-muted-foreground',
                )}
              >
                {change}
              </span>
            ) : null}
            <DeltaDisplay deltaPct={deltaPct} invertDelta={invertDelta} />
            {comparison ? <span className="text-xs text-muted-foreground">{comparison}</span> : null}
          </div>
        ) : null}
      </div>

      {Icon || iconNode ? (
        <div className={cn('metric-card__icon', resolvedIconClassName)} aria-hidden="true">
          {Icon ? <Icon className="h-4 w-4 sm:h-[17px] sm:w-[17px]" /> : iconNode}
        </div>
      ) : null}
    </div>
  )
}
